import React, { useEffect, useState } from "react";
import { Target, Play, Loader2, Plus, Trash2, AlertTriangle, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";

/**
 * Wave BL — Process Optimization Tab.
 *
 * Lets the engineer configure decision variables (with lo/hi/initial),
 * constraints (e.g. purity >= 0.95), pick an optimizer method (SLSQP /
 * differential_evolution / Nelder-Mead), and view the baseline vs
 * optimum comparison + improvement percentage.
 */
export default function OptimizationTab({ projectId, runId }) {
  const [presets, setPresets] = useState({});
  const [preset, setPreset] = useState("minimize_energy");
  const [method, setMethod] = useState("SLSQP");
  const [variables, setVariables] = useState([
    { name: "reflux_ratio", lo: 1.05, hi: 5.0, initial: 3.0 },
    { name: "feed_T_c",     lo: 60, hi: 140, initial: 100 },
  ]);
  const [constraints, setConstraints] = useState([
    { name: "purity >= 0.95", expression: "purity >= 0.95" },
  ]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/sim/optimize/presets")
      .then((r) => setPresets(r.data?.presets || {}))
      .catch(() => {});
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    const p = presets[key];
    if (!p) return;
    if (Array.isArray(p.typical_variables)) {
      setVariables(p.typical_variables.map((v) => ({ ...v })));
    }
    if (Array.isArray(p.typical_constraints)) {
      setConstraints(p.typical_constraints.map((c) => ({
        name: c.name,
        expression: c.expression ? `${c.expression} ${c.kind === "le" ? "<=" : ">="} 0` : c.name,
      })));
    }
  };

  const addVariable = () => setVariables([...variables, {
    name: `var${variables.length + 1}`, lo: 0, hi: 1, initial: 0.5,
  }]);
  const removeVariable = (i) => setVariables(variables.filter((_, j) => j !== i));
  const updateVariable = (i, k, v) => {
    const next = [...variables];
    next[i] = { ...next[i], [k]: k === "name" ? v : Number(v) };
    setVariables(next);
  };

  const addConstraint = () => setConstraints([...constraints, {
    name: "new_constraint", expression: "purity >= 0.95",
  }]);
  const removeConstraint = (i) => setConstraints(constraints.filter((_, j) => j !== i));
  const updateConstraint = (i, k, v) => {
    const next = [...constraints];
    next[i] = { ...next[i], [k]: v };
    setConstraints(next);
  };

  const run = async () => {
    setRunning(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/sim/optimize/run", {
        objective: preset, method, variables, constraints,
      });
      setResult(r.data);
      if (r.data?.converged) {
        toast.success(`Optimizer found ${r.data.improvement_pct.toFixed(1)}% improvement`);
      } else {
        toast.warning("Optimizer terminated without full convergence");
      }
    } catch (e) {
      const detail = formatErr(e.response?.data?.detail) || e.message;
      setErr(detail);
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  const inputSx = {
    padding: "6px 8px", fontSize: 11,
    background: "var(--pf-surface)", color: "var(--pf-text)",
    border: "1px solid var(--pf-border)", borderRadius: 4,
  };

  return (
    <div data-testid="autosim-optimize-panel" style={{ padding: "8px 4px" }}>
      {/* Preset + method controls */}
      <div style={{
        display: "flex", gap: 10, alignItems: "flex-end",
        padding: 12, border: "1px solid var(--pf-border)", borderRadius: 6,
        background: "rgba(255, 159, 10, 0.04)", marginBottom: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
            <Target size={11} style={{ display: "inline", marginRight: 4 }} />
            Objective Preset
          </div>
          <select
            data-testid="optimize-preset-select"
            value={preset}
            onChange={(e) => applyPreset(e.target.value)}
            style={{ ...inputSx, width: "100%" }}
          >
            {Object.entries(presets).map(([k, p]) => (
              <option key={k} value={k}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Method</div>
          <select
            data-testid="optimize-method-select"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            style={{ ...inputSx, width: 180 }}
          >
            <option value="SLSQP">SLSQP (gradient-based, w/ constraints)</option>
            <option value="differential_evolution">Differential Evolution (global)</option>
            <option value="Nelder-Mead">Nelder-Mead (simplex)</option>
          </select>
        </div>
        <button
          data-testid="optimize-run-btn"
          type="button"
          onClick={run}
          disabled={running || variables.length === 0}
          style={{
            padding: "8px 14px", fontSize: 12, fontWeight: 500,
            background: running ? "var(--pf-border)" : "var(--pf-spark)",
            color: "#001122", border: 0, borderRadius: 4,
            cursor: running ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
          {running ? "Optimizing…" : "Run Optimization"}
        </button>
      </div>

      {/* Variables */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 500 }}>Decision Variables</div>
          <button type="button" onClick={addVariable}
              data-testid="optimize-add-var"
              style={{ fontSize: 11, padding: "3px 8px", background: "transparent",
                          color: "var(--pf-spark)", border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer", display: "flex",
                          alignItems: "center", gap: 4 }}>
            <Plus size={11} /> Add
          </button>
        </div>
        <div style={{ border: "1px solid var(--pf-border)", borderRadius: 6, overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(90,200,250,0.04)" }}>
              <tr>
                <th style={{ padding: "7px 10px", textAlign: "left" }}>Variable</th>
                <th style={{ padding: "7px 10px", textAlign: "right", width: 90 }}>Lo</th>
                <th style={{ padding: "7px 10px", textAlign: "right", width: 90 }}>Hi</th>
                <th style={{ padding: "7px 10px", textAlign: "right", width: 100 }}>Initial</th>
                {result && <th style={{ padding: "7px 10px", textAlign: "right", width: 110 }}>Optimum</th>}
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {variables.map((v, i) => (
                <tr key={i} data-testid={`optimize-var-row-${i}`}
                    style={{ borderTop: "1px solid var(--pf-border)" }}>
                  <td style={{ padding: "5px 10px" }}>
                    <input value={v.name}
                        onChange={(e) => updateVariable(i, "name", e.target.value)}
                        style={{ ...inputSx, width: "100%" }} />
                  </td>
                  <td style={{ padding: "5px 10px" }}>
                    <input type="number" value={v.lo} step={0.01}
                        onChange={(e) => updateVariable(i, "lo", e.target.value)}
                        style={{ ...inputSx, width: "100%", textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "5px 10px" }}>
                    <input type="number" value={v.hi} step={0.01}
                        onChange={(e) => updateVariable(i, "hi", e.target.value)}
                        style={{ ...inputSx, width: "100%", textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "5px 10px" }}>
                    <input type="number" value={v.initial} step={0.01}
                        onChange={(e) => updateVariable(i, "initial", e.target.value)}
                        style={{ ...inputSx, width: "100%", textAlign: "right" }} />
                  </td>
                  {result && (
                    <td style={{ padding: "7px 10px", textAlign: "right",
                                    color: "var(--pf-spark)", fontWeight: 500 }}>
                      {(result.optimal_variables?.[v.name] ?? 0).toFixed(3)}
                    </td>
                  )}
                  <td style={{ padding: "5px 8px" }}>
                    <button type="button" onClick={() => removeVariable(i)}
                        data-testid={`optimize-remove-var-${i}`}
                        style={{ background: "transparent", border: 0,
                                    cursor: "pointer", color: "#ff6e6e" }}>
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Constraints */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 500 }}>Constraints</div>
          <button type="button" onClick={addConstraint}
              data-testid="optimize-add-constraint"
              style={{ fontSize: 11, padding: "3px 8px", background: "transparent",
                          color: "var(--pf-spark)", border: "1px solid var(--pf-border)",
                          borderRadius: 4, cursor: "pointer" }}>
            <Plus size={11} /> Add
          </button>
        </div>
        <div style={{ border: "1px solid var(--pf-border)", borderRadius: 6, overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead style={{ background: "rgba(90,200,250,0.04)" }}>
              <tr>
                <th style={{ padding: "7px 10px", textAlign: "left", width: 160 }}>Name</th>
                <th style={{ padding: "7px 10px", textAlign: "left" }}>Expression (e.g. "purity &gt;= 0.95")</th>
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {constraints.map((c, i) => (
                <tr key={i} data-testid={`optimize-constraint-row-${i}`}
                    style={{ borderTop: "1px solid var(--pf-border)" }}>
                  <td style={{ padding: "5px 10px" }}>
                    <input value={c.name}
                        onChange={(e) => updateConstraint(i, "name", e.target.value)}
                        style={{ ...inputSx, width: "100%" }} />
                  </td>
                  <td style={{ padding: "5px 10px" }}>
                    <input value={c.expression}
                        onChange={(e) => updateConstraint(i, "expression", e.target.value)}
                        style={{ ...inputSx, width: "100%", fontFamily: "monospace" }} />
                  </td>
                  <td style={{ padding: "5px 8px" }}>
                    <button type="button" onClick={() => removeConstraint(i)}
                        style={{ background: "transparent", border: 0,
                                    cursor: "pointer", color: "#ff6e6e" }}>
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Errors */}
      {err && (
        <div data-testid="optimize-error" style={{
          padding: 10, borderRadius: 6, fontSize: 12,
          background: "rgba(255, 69, 58, 0.1)", color: "#ff6e6e",
          border: "1px solid rgba(255, 69, 58, 0.35)", marginBottom: 12,
        }}>
          <AlertTriangle size={12} style={{ display: "inline", marginRight: 6 }} />
          {err}
        </div>
      )}

      {/* Results */}
      {result && (
        <div data-testid="optimize-results" style={{
          padding: 16, borderRadius: 6, fontSize: 12,
          background: "rgba(50, 215, 75, 0.05)",
          border: "1px solid rgba(50, 215, 75, 0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <TrendingDown size={14} style={{ color: "#32d74b" }} />
            <strong>Optimization Result</strong>
            <span style={{ opacity: 0.7 }}>· {result.method} · {result.n_evaluations} evaluations</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            <div style={{ padding: 10, background: "rgba(0,0,0,0.2)", borderRadius: 4 }}>
              <div style={{ fontSize: 10, opacity: 0.7 }}>Baseline</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {result.baseline_value?.toFixed(2)}
              </div>
            </div>
            <div style={{ padding: 10, background: "rgba(0,0,0,0.2)", borderRadius: 4 }}>
              <div style={{ fontSize: 10, opacity: 0.7 }}>Optimum</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#32d74b" }}>
                {result.optimum_value?.toFixed(2)}
              </div>
            </div>
            <div style={{ padding: 10, background: "rgba(0,0,0,0.2)", borderRadius: 4 }}>
              <div style={{ fontSize: 10, opacity: 0.7 }}>Improvement</div>
              <div style={{ fontSize: 14, fontWeight: 500,
                               color: result.improvement_pct > 0 ? "#32d74b" : "#ff9f0a" }}>
                {result.improvement_pct?.toFixed(1)}%
              </div>
            </div>
          </div>
          {result.final_result && (
            <div style={{ marginTop: 10, fontSize: 11, opacity: 0.8 }}>
              Final state: {Object.entries(result.final_result).slice(0, 5).map(([k, v]) => (
                <span key={k} style={{ marginRight: 12 }}>
                  <strong>{k}</strong>: {typeof v === "number" ? v.toFixed(3) : String(v)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
